import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileLines, faEye } from '@fortawesome/free-solid-svg-icons';
import type { ReportReviewQueueItem } from '@/services/dashboard.service';
import { ROUTES } from '@/routes/routes';

function fmtDateTime(v: string | null | undefined): string {
  if (!v) return '—';
  return new Date(v).toLocaleString();
}

function hoursWaiting(submittedAt: string | null | undefined): string {
  if (!submittedAt) return '—';
  const ms = Date.now() - new Date(submittedAt).getTime();
  const hours = Math.max(0, Math.floor(ms / 3_600_000));
  if (hours < 1) return '< 1h';
  if (hours < 48) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
}

interface ReportReviewsTableProps {
  items: ReportReviewQueueItem[];
}

/**
 * B6.17 — Service Reports awaiting the OPS decision.
 *
 * A SEPARATE table from OperationsReviewsTable, per DEC-1: the two queues act on
 * different objects with different verbs, and one shared table would give the
 * operator an "Approve" button whose meaning depended on which half of the list
 * the row happened to be in.
 *
 * There is no inline Approve/Reject here, deliberately. A report decision is made
 * against the rendered document — you approve what you have read — so the only
 * action is Review, which opens the inspection where the existing report workflow
 * panel lives. That panel is untouched by B6.17.
 */
export function ReportReviewsTable({ items }: ReportReviewsTableProps) {
  const navigate = useNavigate();

  const openReport = (inspectionId: string) =>
    navigate(ROUTES.INSPECTION_DETAIL.replace(':id', inspectionId));

  return (
    <div className="rounded-xl border border-border bg-card">
      <div className="flex items-center justify-between px-5 py-3.5 border-b border-border">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-foreground">
          <FontAwesomeIcon icon={faFileLines} className="h-3.5 w-3.5 text-muted-foreground" />
          Service Reports Awaiting Review
        </h3>
        <span
          className={
            items.length > 0
              ? 'rounded-full bg-amber-500/15 px-2.5 py-0.5 text-xs font-semibold text-amber-600 dark:text-amber-400'
              : 'rounded-full bg-muted px-2.5 py-0.5 text-xs font-medium text-muted-foreground'
          }
        >
          {items.length}
        </span>
      </div>

      {items.length === 0 ? (
        <p className="px-5 py-8 text-sm text-muted-foreground text-center">
          No Service Reports awaiting review.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs font-medium text-muted-foreground">
                <th className="px-5 py-2.5">Report Number</th>
                <th className="px-5 py-2.5">Job Number</th>
                <th className="px-5 py-2.5 hidden sm:table-cell">Customer</th>
                <th className="px-5 py-2.5 hidden md:table-cell">Equipment</th>
                <th className="px-5 py-2.5 hidden lg:table-cell">Submitted By</th>
                <th className="px-5 py-2.5 hidden lg:table-cell">Submitted</th>
                <th className="px-5 py-2.5 text-right hidden md:table-cell">Waiting</th>
                <th className="px-5 py-2.5">Status</th>
                <th className="px-5 py-2.5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {items.map((item) => (
                <tr key={item.id} className="hover:bg-muted/30">
                  <td className="px-5 py-3 font-medium text-primary">{item.reportNumber ?? '—'}</td>
                  <td className="px-5 py-3 text-foreground">{item.inspectionNumber}</td>
                  <td className="px-5 py-3 text-foreground hidden sm:table-cell">
                    {item.customerOrganizationName ?? item.customerName ?? '—'}
                  </td>
                  <td className="px-5 py-3 text-muted-foreground hidden md:table-cell">
                    {item.equipmentName ?? '—'}
                  </td>
                  <td className="px-5 py-3 text-muted-foreground hidden lg:table-cell">
                    {item.submittedByName ?? '—'}
                  </td>
                  <td className="px-5 py-3 text-muted-foreground hidden lg:table-cell">
                    {fmtDateTime(item.submittedAt)}
                  </td>
                  <td className="px-5 py-3 text-right text-muted-foreground hidden md:table-cell">
                    {hoursWaiting(item.submittedAt)}
                  </td>
                  <td className="px-5 py-3">
                    <span className="inline-flex items-center rounded-full bg-amber-500/15 px-2 py-0.5 text-xs font-medium text-amber-600 dark:text-amber-400">
                      Under Review
                    </span>
                  </td>
                  <td className="px-5 py-3 text-right">
                    <button
                      type="button"
                      onClick={() => openReport(item.inspectionId)}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-border px-2.5 py-1.5 text-xs font-medium text-foreground hover:bg-muted"
                      aria-label={`Review report ${item.reportNumber ?? item.inspectionNumber}`}
                    >
                      <FontAwesomeIcon icon={faEye} className="h-3 w-3" />
                      Review
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function ReportReviewsTableSkeleton() {
  return (
    <div className="rounded-xl border border-border bg-card">
      <div className="px-5 py-3.5 border-b border-border">
        <div className="h-4 w-56 animate-pulse rounded bg-muted" />
      </div>
      <div className="space-y-3 p-5">
        {[0, 1, 2].map((i) => (
          <div key={i} className="h-8 animate-pulse rounded bg-muted" />
        ))}
      </div>
    </div>
  );
}
